import { Close, Done, Remove } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import type React from 'react';
import { useMemo } from 'react';
import ChipLabel, { type CHIP_COLORS } from '/@/components/chip-label';
import type { Activity, ActivityAttendance, ActivityParticipant } from '/@/hooks/activities';
import { parseUserNode, type UserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { getInitials, ROLE_LABELS } from '../../constants';

const ATTENDANCE_CHIP_STYLES: Record<
    ActivityAttendance,
    { bg: string; color: string; label: string; icon: React.ReactNode }
> = {
    si: { bg: '#EEFFEF', color: '#01830C', label: _('Si'), icon: <Done sx={{ width: 18, height: 18 }} /> },
    parcial: {
        bg: '#EEFFEF',
        color: '#01830C',
        label: _('Parcial'),
        icon: <Remove sx={{ width: 18, height: 18 }} />,
    },
    no: { bg: '#FFE4CD', color: '#B85302', label: _('No'), icon: <Close sx={{ width: 18, height: 18 }} /> },
};

const AttendanceChip = ({ attendance }: { attendance: ActivityAttendance }) => {
    const style = ATTENDANCE_CHIP_STYLES[attendance];
    return (
        <Stack
            direction='row'
            spacing={0.5}
            alignItems='center'
            sx={{
                bgcolor: style.bg,
                color: style.color,
                border: '1px solid rgba(16, 56, 79, 0.12)',
                borderRadius: '100px',
                pl: 0.5,
                pr: 1,
                py: 0.5,
                flexShrink: 0,
            }}
        >
            {style.icon}
            <Typography variant='subtitle2'>{style.label}</Typography>
        </Stack>
    );
};

const UserCard = ({
    node,
    otherName,
    squad,
    badgeColor,
    attendance,
    notes,
}: {
    node: UserNode | null;
    otherName?: string;
    squad?: string | null;
    badgeColor: { bg: string; color: string };
    attendance?: ActivityAttendance;
    notes?: string[];
}) => {
    const displayName = node?.name ?? otherName ?? '';
    return (
        <Stack
            spacing={1}
            sx={{
                border: '1px solid rgba(16, 56, 79, 0.12)',
                borderRadius: '8px',
                boxShadow:
                    '0px 1px 0.5px rgba(0,0,0,0.14), 0px 2px 0.5px rgba(0,0,0,0.12), 0px 1px 1.5px rgba(0,0,0,0.2)',
                p: 2,
                width: '100%',
            }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent='space-between'
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={{ xs: 1, sm: 0 }}
            >
                <Stack direction='row' spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ minWidth: 0 }}>
                    <Box
                        sx={{
                            bgcolor: '#F5F5F5',
                            borderRadius: '50%',
                            width: 40,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: '14px',
                            flexShrink: 0,
                        }}
                        color='primary'
                    >
                        {getInitials(displayName)}
                    </Box>
                    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                        <Stack direction='row' spacing={1} alignItems='center'>
                            <ChipLabel
                                type={node ? (node.type as keyof typeof CHIP_COLORS) : 'gray'}
                                label={node ? ROLE_LABELS[node.type] : _('Otros')}
                            />
                            {squad && (
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        color: 'rgba(16, 56, 79, 0.73)',
                                        letterSpacing: '1.25px',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {squad}
                                </Typography>
                            )}
                        </Stack>
                        <Typography sx={{ fontWeight: 700, wordBreak: 'break-word' }} color='primary'>
                            {displayName}
                        </Typography>
                    </Stack>
                </Stack>
                {attendance && <AttendanceChip attendance={attendance} />}
            </Stack>
            {notes?.map((note, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: notes have no stable id
                <Box key={index} sx={{ bgcolor: '#F5F5F5', borderRadius: '4px', p: 1 }}>
                    <Typography sx={{ fontSize: '12px' }} color='primary'>
                        {note}
                    </Typography>
                </Box>
            ))}
        </Stack>
    );
};

const Section = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <Stack spacing={1} sx={{ width: '100%' }}>
        <Typography sx={{ fontSize: '16px', fontWeight: 700, letterSpacing: '0.15px' }} color='primary'>
            {label}
        </Typography>
        {children}
    </Stack>
);

const attendanceTally = (participants: ActivityParticipant[]) => ({
    si: participants.filter((p) => p.attendance === 'si').length,
    parcial: participants.filter((p) => p.attendance === 'parcial').length,
    no: participants.filter((p) => p.attendance === 'no').length,
});

/**
 * The body of an activity's detail — topics/budget, place, speakers, participants and note.
 * Shared verbatim between the in-app detail modal and the downloadable PDF.
 */
const ActivityDetailContent = ({ activity }: { activity: Activity }) => {
    // Speakers/participants come straight from the API without a computed `name` (the
    // backend's UserNodeSerializer never returns one); parse them like every other node.
    // Both lists can also hold free-text "Otros" entries with no backing node.
    const speakers = useMemo(
        () => [
            ...activity.speakers.map((node) => ({
                key: `node-${node.id}`,
                node: parseUserNode(node) as UserNode | null,
                otherName: undefined as string | undefined,
            })),
            ...activity.otherSpeakers.map((name, index) => ({
                key: `other-${index}`,
                node: null as UserNode | null,
                otherName: name as string | undefined,
            })),
        ],
        [activity.speakers, activity.otherSpeakers]
    );
    const participants = useMemo(
        () =>
            activity.participants.map((participant, index) => ({
                ...participant,
                key: participant.userNode ? `node-${participant.userNode.id}` : `other-${index}`,
                userNode: participant.userNode ? parseUserNode(participant.userNode) : null,
            })),
        [activity.participants]
    );
    const tally = useMemo(() => attendanceTally(activity.participants), [activity.participants]);

    return (
        <Stack spacing={3} sx={{ width: '100%' }}>
            <Typography variant='h5' color='primary'>
                {activity.name}
            </Typography>

            <Stack direction='row' spacing={4} sx={{ width: '100%' }}>
                <Stack spacing={1} sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '16px' }} color='primary'>
                        {_('Temas a tratar')}
                    </Typography>
                    <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
                        {activity.topics.map((topic) => (
                            <Typography key={topic} component='li' variant='subtitle2' color='primary'>
                                {topic}
                            </Typography>
                        ))}
                    </Box>
                </Stack>
                <Stack spacing={1} sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '16px' }} color='primary'>
                        {_('Presupuesto')}
                    </Typography>
                    <Typography variant='subtitle2' color='primary'>
                        {activity.budget ? `$${activity.budget}` : '-'}
                    </Typography>
                </Stack>
            </Stack>

            {activity.place && (
                <Section label={_('Lugar')}>
                    <Typography variant='subtitle2' color='primary'>
                        {activity.place}
                    </Typography>
                </Section>
            )}

            {speakers.length > 0 && (
                <Section label={_('Expositores')}>
                    <Stack spacing={1} sx={{ width: '100%' }}>
                        {speakers.map((speaker) => (
                            <UserCard
                                key={speaker.key}
                                node={speaker.node}
                                otherName={speaker.otherName}
                                badgeColor={{ bg: '#B8F2ED', color: '#084D47' }}
                            />
                        ))}
                    </Stack>
                </Section>
            )}

            <Section label={_('Participantes')}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent='space-between'
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    sx={{ width: '100%' }}
                >
                    <Typography sx={{ fontSize: '12px', fontWeight: 700 }} color='primary'>
                        {_('{{count}} participantes', { count: participants.length })}
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ md: 2 }}>
                        <Typography color='primary.mui.inactive' variant='subtitle1'>
                            {_('Asistencia')}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#01830C' }}>
                            <b>{tally.si}</b> {_('Si')}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#01830C' }}>
                            <b>{tally.parcial}</b> {_('Parcial')}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#B85302' }}>
                            <b>{tally.no}</b> {_('No')}
                        </Typography>
                    </Stack>
                </Stack>
                <Stack spacing={1} sx={{ width: '100%' }}>
                    {participants.map((participant) => (
                        <UserCard
                            key={participant.key}
                            node={participant.userNode}
                            otherName={participant.otherName}
                            squad={participant.squad}
                            badgeColor={{ bg: '#C2E0FF', color: '#0B3F73' }}
                            attendance={participant.attendance}
                            notes={participant.notes}
                        />
                    ))}
                </Stack>
            </Section>

            {activity.note && (
                <Section label={_('Nota')}>
                    <Typography variant='subtitle2' color='primary'>
                        {activity.note}
                    </Typography>
                </Section>
            )}
        </Stack>
    );
};

export default ActivityDetailContent;
